import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, DollarSign, Calendar, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import ModalPortal from './ModalPortal';

const ChatOrderModal = ({ isOpen, onClose, recipient, conversationId, onSuccess, isNegotiating = false, initialData = null, portfolioId = null }) => {
  const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [duration, setDuration] = useState('');
    const [deliverables, setDeliverables] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchingPortfolio, setFetchingPortfolio] = useState(false);

    useEffect(() => {
        const fetchPortfolio = async () => {
            if (isOpen && portfolioId && !isNegotiating && !initialData) {
                try {
                    setFetchingPortfolio(true);
                    const response = await api.get(`/portfolio/${portfolioId}`);
                    const portfolio = response.data.data;
                    if (portfolio) {
                        setTitle(`Custom Order: ${portfolio.title}`);
                        setDescription(`Based on portfolio item: ${portfolio.description}`);
                        setDeliverables(portfolio.files?.map(f => f.filename).join(', ') || '');
                    }
                } catch (error) {
                    console.error('Error fetching portfolio for pre-fill:', error);
                } finally {
                    setFetchingPortfolio(false);
                }
            }
        };

        fetchPortfolio();
    }, [isOpen, portfolioId, isNegotiating, initialData]);

    useEffect(() => {
        if (isOpen && initialData) {
            setTitle(initialData.title || '');
            setDescription(initialData.description || '');
            setAmount(initialData.amount?.toString() || '');
            setDuration(initialData.duration || '');
            setDeliverables(initialData.deliverables || '');
        } else if (isOpen && !initialData) {
            // Reset fields for new proposal
            setTitle('');
            setDescription('');
            setAmount('');
            setDuration('');
            setDeliverables('');
        }
    }, [isOpen, initialData]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title || !description || !amount) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            if (isNegotiating) {
                await api.patch(`/chat-orders/${initialData.id || initialData._id}/respond`, {
                    action: 'negotiate',
                    amount: parseFloat(amount),
                    description
                });
                toast.success('Counter-proposal sent!');
            } else {
                await api.post('/chat-orders', {
                    conversationId,
                    recipientId: recipient.id || recipient._id,
                    title,
                    description,
                    amount: parseFloat(amount),
                    duration,
                    deliverables
                });
                toast.success('Order proposal sent!');
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('Error with chat order:', error);
            toast.error(error.response?.data?.message || 'Failed to process proposal');
        } finally {
            setLoading(false);
        }
    };

    const platformFee = amount ? (parseFloat(amount) * 0.1).toFixed(2) : '0.00';
    const giggerEarnings = amount ? (parseFloat(amount) * 0.9).toFixed(2) : '0.00';

    return (
        <ModalPortal>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
                <div className="bg-blue-600 px-6 py-4 flex items-center justify-between text-white">
                    <h2 className="text-xl font-bold">{isNegotiating ? 'Negotiate Order' : 'New Order Proposal'}</h2>
                    <button onClick={onClose} className="p-1 hover:bg-blue-500 rounded-full transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <p className="text-sm text-gray-600 mb-4">
                        {isNegotiating
                            ? `Send a counter-proposal to ${recipient?.username}.`
                            : `Propose an order to ${recipient?.username}.`
                        }
                        Once accepted and paid, this becomes a full GigShip order.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Order Title *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Logo Design for Website"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            required
                            disabled={isNegotiating} // Title usually remains fixed across negotiations
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('orders.what_will_you_do', 'What exactly will you deliver? Describe your offer in detail.')}
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (GH¢) *</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">GH¢</span>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="50.00"
                                    className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.duration', 'Duration')}</label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    placeholder="e.g. 3 days"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                    disabled={isNegotiating}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.deliverables', 'Deliverables (Optional)')}</label>
                        <input
                            type="text"
                            value={deliverables}
                            onChange={(e) => setDeliverables(e.target.value)}
                            placeholder="e.g. 2 PNG files, 1 Source file"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            disabled={isNegotiating}
                        />
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                        <div className="flex justify-between text-gray-600">
                            <span>Platform Fee (10%)</span>
                            <span>- GH¢{platformFee}</span>
                        </div>
                        <div className="flex justify-between font-bold text-gray-900 pt-1 border-t border-gray-200">
                            <span>{t('orders.gigger_earnings', 'Your Earnings')}</span>
                            <span>GH¢{giggerEarnings}</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-3 rounded-lg font-bold text-white transition-all ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 active:scale-95 shadow-lg hover:shadow-blue-200'
                            }`}
                    >
                        {loading || fetchingPortfolio
                            ? (isNegotiating ? 'Sending Counter...' : 'Processing...')
                            : (isNegotiating ? 'Send Counter-Proposal' : 'Send Order Proposal')
                        }
                    </button>

                    <p className="text-[10px] text-center text-gray-400 italic">
                        {isNegotiating
                            ? "This will update the existing proposal and reset the 3-day expiry."
                            : "This proposal will expire in 3 days if not responded to."
                        }
                    </p>
                </form>
            </div>
        </div>
        </ModalPortal>
    );
};

export default ChatOrderModal;
